// What an institution collects and may share, followed over time
// (docs/ref-belonging-plan.md, step 3). A dataset is declared in the Pod's
// datasets.json as an id, a label and its record classes; everything else is
// derived. A source contributes to a dataset when its mapping writes one of
// the dataset's record classes, and a cube belongs to the dataset that holds
// the terms it counts. A cube across two datasets is refused: comparing them
// is a query over cubes from each.
//
// The dataset replaces the cube's old `theme`: it groups the cubes and is what
// a partner is granted. Its id is what a partner's grant names.

const SLUG = /^[a-z][a-z0-9-]*$/;

export const EMPTY_DATASETS = { datasets: [] };

// Small numbers, per dataset (docs/ref-belonging-plan.md, step 7.6): a count
// under `suppressBelow` is hidden where terms are combined, and a term on its
// own is shared whole for a period when at least `minPerColumn` records have
// a value for it (null: hidden like a combination). A new dataset starts at
// 5 and 10. A record class no dataset declares is held to cell suppression
// at 5.
export const NEW_DATASET_RULES = { suppressBelow: 5, minPerColumn: 10 };
const IMPLICIT_RULES = { suppressBelow: 5, minPerColumn: null };
const isCount = (n) => Number.isInteger(n) && n >= 0;
export const rulesOf = (dataset) => dataset
  ? { suppressBelow: dataset.suppressBelow, minPerColumn: dataset.minPerColumn ?? null } : IMPLICIT_RULES;
export function rulesProblems({ suppressBelow, minPerColumn } = {}) {
  return [
    isCount(suppressBelow) ? null : 'suppressBelow must be a whole number, 0 or more',
    minPerColumn == null || isCount(minPerColumn) ? null : 'minPerColumn must be a whole number, 0 or more, or null',
  ].filter(Boolean);
}

export function validateDatasets(doc) {
  const errors = [];
  if (!Array.isArray(doc?.datasets)) return ['datasets must be an array'];
  const ids = new Set();
  const owner = new Map();
  for (const [i, d] of doc.datasets.entries()) {
    const at = `datasets[${i}]`;
    if (!SLUG.test(d?.id ?? '')) errors.push(`${at}.id must be lowercase letters, digits and hyphens`);
    else if (ids.has(d.id)) errors.push(`${at}.id is duplicated`);
    else ids.add(d.id);
    if (!String(d?.label ?? '').trim()) errors.push(`${at}.label is required`);
    errors.push(...rulesProblems(d ?? {}).map((problem) => `${at}.${problem}`));
    if (!Array.isArray(d?.recordClasses) || !d.recordClasses.length) {
      errors.push(`${at}.recordClasses is required`);
      continue;
    }
    for (const recordClass of d.recordClasses) {
      if (owner.has(recordClass)) errors.push(`${at}: ${recordClass} is already in ${owner.get(recordClass)}`);
      else owner.set(recordClass, d.id);
    }
  }
  return errors;
}

// A record class no dataset declares is a dataset of its own until someone
// groups it, so a source mapped before its dataset was declared still has
// cubes that belong somewhere. Its id comes from the class's local name.
const implicitId = (recordClass) => recordClass.split(/[:#/]/).at(-1)
  .replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase().replace(/[^a-z0-9-]/g, '-') || 'records';

// The declared datasets plus one per undeclared record class the mappings
// write. `mapping` is one mapping or several combined ({ records, columns }).
export function resolveDatasets(doc, mapping) {
  const declared = (doc?.datasets ?? []).map((d) => ({ ...d, minPerColumn: d.minPerColumn ?? null, declared: true }));
  const claimed = new Set(declared.flatMap((d) => d.recordClasses));
  const taken = new Set(declared.map((d) => d.id));
  const implicit = [];
  for (const recordClass of Object.keys(mapping?.records ?? {})) {
    if (claimed.has(recordClass)) continue;
    let id = implicitId(recordClass);
    while (taken.has(id)) id += '-records';
    taken.add(id);
    implicit.push({ id, label: recordClass, recordClasses: [recordClass], ...IMPLICIT_RULES, declared: false });
  }
  return [...declared, ...implicit];
}

// Where a dataset's records live in the Pod: a container named after its id,
// for every record class and every source in it (docs/ref-belonging-plan.md,
// step 7.3). The id, not the label, decides it, so renaming a dataset moves
// nothing. Sources sharing a container do not clash: each batch file is named
// after its source and record class, and an ingest only clears its own.
// Derived, never stored: a mapping read from the Pod gets its containers
// here, and loses them again when written (withoutContainers).
export const recordContainer = (datasetId) => `${datasetId}/`;

export function withContainers(mapping, doc) {
  if (!mapping?.records) return mapping;
  const datasets = resolveDatasets(doc, mapping);
  const datasetOf = new Map(datasets.flatMap((d) => d.recordClasses.map((c) => [c, d.id])));
  return { ...mapping, records: Object.fromEntries(Object.entries(mapping.records).map(([recordClass, location]) =>
    [recordClass, { ...location, container: recordContainer(datasetOf.get(recordClass)) }])) };
}

export function withoutContainers(mapping) {
  if (!mapping?.records) return mapping;
  return { ...mapping, records: Object.fromEntries(Object.entries(mapping.records)
    .map(([recordClass, { container, ...location }]) => [recordClass, location])) };
}

// The terms a cube counts: its dimensions, and the measure a sum adds up. The
// period is shared by every record and belongs to no dataset.
export function cubeTerms(aggregate, aggregations) {
  const reducer = aggregate.reducer ?? 'count';
  const measure = aggregate.measure ?? aggregations?.measure;
  return [...(aggregate.dimensions ?? []), ...(reducer === 'sum' && measure ? [measure] : [])];
}

const recordClassesOf = (term, mapping) => new Set((mapping?.columns ?? [])
  .filter((c) => c.target === term && c.record && c.record !== 'shared').map((c) => c.record));

// The dataset a cube belongs to, or the reason it belongs to none. A term the
// mappings don't write is left to validateAggregations, which reports it.
export function datasetOfCube(aggregate, aggregations, mapping, datasets) {
  const byClass = new Map(datasets.flatMap((d) => d.recordClasses.map((c) => [c, d.id])));
  const found = new Map();
  for (const term of cubeTerms(aggregate, aggregations)) {
    for (const recordClass of recordClassesOf(term, mapping)) {
      const id = byClass.get(recordClass);
      if (id) (found.get(id) ?? found.set(id, new Set()).get(id)).add(term);
    }
  }
  if (found.size <= 1) return { dataset: [...found.keys()][0] ?? null, problem: null };
  const parts = [...found].map(([id, terms]) => `${[...terms].join(', ')} in ${id}`);
  return { dataset: null, problem: `counts terms from more than one dataset (${parts.join('; ')}); compare them in a query instead` };
}

// The aggregations with each cube's dataset filled in, and the small-number
// rules it is counted under (its dataset's), for counting, the screens and the
// publisher. Derived, so never written back: see withoutDatasets.
export function withDatasets(aggregations, mapping, datasets) {
  return { ...aggregations, aggregates: (aggregations?.aggregates ?? []).map((a) => {
    const dataset = datasetOfCube(a, aggregations, mapping, datasets).dataset;
    return { ...a, dataset, ...rulesOf(datasets.find((d) => d.id === dataset)) };
  }) };
}

export function withoutDatasets(aggregations) {
  return { ...aggregations, aggregates: (aggregations?.aggregates ?? [])
    .map(({ dataset, suppressBelow, minPerColumn, ...a }) => a) };
}

// Each dataset with what it is made of: its record classes and where they
// live, the sources that write them (and which columns), and the cubes that
// count its terms. `sources` is [{ id, mapping }].
export function describeDatasets(datasets, sources, aggregations) {
  const combined = {
    records: Object.assign({}, ...sources.map((s) => s.mapping?.records ?? {})),
    columns: sources.flatMap((s) => s.mapping?.columns ?? []),
  };
  return datasets.map((d) => {
    const classes = new Set(d.recordClasses);
    return {
      ...d,
      recordClasses: d.recordClasses.map((id) => ({ id, container: combined.records[id]?.container ?? null })),
      sources: sources.map((s) => ({
        id: s.id,
        columns: (s.mapping?.columns ?? []).filter((c) => classes.has(c.record)).map((c) => c.source),
      })).filter((s) => s.columns.length),
      cubes: (aggregations?.aggregates ?? [])
        .filter((a) => datasetOfCube(a, aggregations, combined, datasets).dataset === d.id).map((a) => a.id),
    };
  });
}
